<template>
  <div class="container order-container" v-if="order">
    <h1 class="order-title title has-text-right is-clearfix">
      <div class="is-pulled-left">
        <strong>{{ companies[order.company].name }}</strong> #{{ order.id }}
      </div>
      <small :class="{ 'show-print': !order.printed }">
        <div class="ar-circle"></div>
        <strong>Printed:</strong>
        <span v-if="order.printed === false">{{ currentDate | moment("MM/DD/YY hh:mm A") }}</span>
        <span v-else>{{ order.printed_at | moment("MM/DD/YY hh:mm A") }}</span>
        &nbsp;&nbsp;
        <div class="ar-square"></div>
      </small>
      <div class="is-pulled-right">
        <strong>{{ order.payment_method === 'Paypal' ? 'PAYPAL' : 'CREDIT CARD' }}</strong>
      </div>
    </h1>
    <hr />
    <div class="columns is-mobile">
      <div class="column is-10 main-order">
        <div v-if="order.voided">
          <h1 class="title has-text-centered has-text-danger">--- ORDER VOIDED ---</h1>
        </div>
        <h2 class="subtitle">
          BILL TO:
          <span class="account-number" v-if="$route.name === 'order'">
                        CUSTOMER #<input type="text" class="input" name="account_number_fix" v-model="account_number">
                        <button type="button" class="button is-primary is-block"
                                v-if="(order.account_number === null && account_number !== '' && account_number !== null) ||
                                (order.account_number !== null && account_number !== order.account_number)"
                                @click="updateAccountNumber()">
                            <div v-if="!updating">SAVE CHANGES</div>
                            <div v-else>SAVING...</div>
                        </button>
                    </span>
          <span class="account-number" v-else>
                        CUSTOMER #<span>{{ order.account_number }}</span>
                    </span>
        </h2>
        <div class="columns order-billing">
          <div class="column is-4">
            <router-link v-if="order.user_id" :to="{name:'user', params:{id:order.user_id}}">
              <strong>{{ order.first_name }} {{ order.last_name }} <i class="fa fa-user"></i></strong>
            </router-link>
            <span v-else>
                            <strong>{{ order.first_name }} {{ order.last_name }}</strong>
                        </span>
            <br />
            <span v-html="highlightAddressNumber(order.line1)"></span>
            <br v-if="order.line2" />
            {{ order.line2 }}
            <br v-if="order.line3" />
            {{ order.line3 }}
            <br />
            {{ order.city }}, {{ order.state }} {{ order.zip }}
            <br />
            {{ order.phone | phone }}
            <br />
            {{ order.email }}
          </div>
          <div class="column is-4 payment-details">
            <div v-if="order.payment_method === 'Credit Card' && order.payload">
              {{ order.payload.name }}
              <br />
              <strong style="letter-spacing: 0.05rem">{{ order.payload.number }}</strong>
              <table>
                <tr>
                  <td width="60px">Exp-Cvc:</td>
                  <td>
                    |
                    <strong>{{ order.payload.month }} {{ order.payload.year }}</strong>
                    &nbsp;|&nbsp;
                    <strong>{{ order.payload.cvc }}</strong>
                    |
                  </td>
                </tr>
                <tr>
                  <td width="60px">Adr-Zip:</td>
                  <td>
                    |
                    <strong>
                      {{ order.line1 | pluckAddressNumber }}
                    </strong>
                    &nbsp;|&nbsp;
                    <strong>
                      {{ order.zip.substr(0, 5) }}
                    </strong>
                    |
                  </td>
                </tr>
              </table>
            </div>
            <div v-else>
              PayPal
              <br />
              <strong>{{ order.paypal_sale_id }}</strong>
              <br />
              {{ order.paypal_first_name }} {{ order.paypal_last_name }}
              <br />
              {{ order.paypal_email }}
            </div>
          </div>
          <div class="column is-3 order-totals">
            <div class="order-total">
              <div class="total-descriptor">Subtotal</div>
              <div class="total-amount">{{ order.subtotal | currency }}</div>
            </div>
            <div class="order-total">
              <div class="total-descriptor">Shipping</div>
              <div class="total-amount">{{ order.shipping | currency }}</div>
            </div>
            <div class="order-total">
              <div class="total-descriptor">Order Total</div>
              <div class="total-amount">{{ order.total | currency }}</div>
            </div>
            <div class="order-total" v-if="order.discount_amount > 0">
              <div class="total-descriptor">Discount ({{ order.discount_code }})</div>
              <div class="total-amount">- {{ order.discount_amount | currency }}</div>
            </div>
            <div class="order-total" v-if="order.discount_amount > 0">
              <div class="total-descriptor">Final Total</div>
              <div class="total-amount">{{ order.discounted_total | currency }}</div>
            </div>
          </div>
        </div>
        <div v-for="(recipient, index) in order.recipients" class="order-shipping">
          <div class="columns is-mobile">
            <div class="column is-4 order-recipient">
              <h2 class="subtitle">
                SHIP TO:
                <span class="consignee">#<span></span></span>
              </h2>
              {{ recipient.first_name }} {{ recipient.last_name }}
              <br />
              {{ recipient.line1 }}
              <br v-if="recipient.line2">
              {{ recipient.line2 }}
              <br v-if="recipient.line3">
              {{ recipient.line3 }}
              <br />
              {{ recipient.city }}, {{ recipient.state }} {{ recipient.zip }}
              <br v-if="recipient.phone" />
              {{ recipient.phone | phone }}
              <br v-if="recipient.email" />
              {{ recipient.email }}
            </div>
            <div class="column is-8 order-items">
              <div class="order-item is-header">
                <div class="order-item--quantity">Qty.</div>
                <div class="order-item--product">
                  Item
                  <div class="is-pulled-right">
                    <small>#{{ order.id }} - {{ index + 1 }}/{{ order.recipients.length }}</small>
                  </div>
                </div>
              </div>
              <div v-for="item in getItemsForRecipient(recipient)">
                <div class="order-item">
                  <div class="order-item--quantity">{{ item.quantity }}</div>
                  <div class="order-item--product">
                    <div class="order-item--product-sku">
                      #{{ item.product_sku }}
                    </div>
                    <div class="order-item--product-price">
                      ({{ item.product_price | currency }})
                    </div>
                    <div class="order-item--product-name">
                      {{ item.product_name }}
                    </div>
                  </div>
                  <div class="order-item--arrival">
                    <strong>Arrival:</strong>
                    <span v-if="typeof item.months !== 'object'">{{ item.months }}</span>
                    <span v-else v-for="(month, index) in item.months">
                                            {{ getMonthName(month) }}
                                            {{ (index + 1) !== item.months.length ? ',' : '' }}
                                        </span>
                  </div>
                  <div class="order-item--greeting" v-if="item.greeting">
                    <strong>Greeting:</strong>
                    <span>{{ item.greeting }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="order-footer">END OF ORDER <strong>#{{ order.id }}</strong></div>
        <div v-if="order.voided">
          <h1 class="title has-text-centered has-text-danger">--- ORDER VOIDED ---</h1>
        </div>
      </div>
      <div class="column is-2 charges">
        <div class="charge has-text-centered">
          <strong>
            {{ order.created_at | moment("M/DD/YY") }}@{{ order.created_at | moment("H:mm") }}
          </strong>
        </div>
        <div class="charge not-zero">Total: <span>{{ order.discounted_total | currency }}</span></div>
        <div class="charge not-zero">
          Rec'd
          <span>{{
              order.payment_method === 'Paypal'
                ? (order.discounted_total !== null ? order.discounted_total : order.total)
                : (parseFloat(order.charges_nov) + parseFloat(order.charges_dec) === 0)
                ? ''
                : parseFloat(order.charges_nov) + parseFloat(order.charges_dec) | currency
            }}</span>
        </div>
        <div class="charge not-zero">Ship: <span v-html="numShip"></span></div>
        <div class="charge">ACK: <span><strong>{{ order.payment_method === 'Paypal' ? '02' : '' }}</strong></span></div>
        <div class="charge-divide">BILLING</div>
        <div :class="{'charge':true, 'not-zero bold': order.charges_nov !== '0.00'}">
          Nov: <span v-if="order.charges_nov !== '0.00'">{{ order.charges_nov | currency }}</span>
        </div>
        <div :class="{'charge':true, 'not-zero bold': order.charges_dec !== '0.00'}">
          Dec: <span v-if="order.charges_dec !== '0.00'">{{ order.charges_dec | currency }}</span>
        </div>
        <div :class="{'charge':true, 'not-zero bold': order.charges_jan !== '0.00'}">
          Jan: <span v-if="order.charges_jan !== '0.00'">{{ order.charges_jan | currency }}</span>
        </div>
        <div :class="{'charge':true, 'not-zero bold': order.charges_feb !== '0.00'}">
          Feb: <span v-if="order.charges_feb !== '0.00'">{{ order.charges_feb | currency }}</span>
        </div>
        <div :class="{'charge':true, 'not-zero bold': order.charges_mar !== '0.00'}">
          Mar: <span v-if="order.charges_mar !== '0.00'">{{ order.charges_mar | currency }}</span>
        </div>
        <div :class="{'charge':true, 'not-zero bold': order.charges_apr !== '0.00'}">
          Apr: <span v-if="order.charges_apr !== '0.00'">{{ order.charges_apr | currency }}</span>
        </div>
        <div :class="{'charge':true, 'not-zero bold': order.charges_may !== '0.00'}">
          May: <span v-if="order.charges_may !== '0.00'">{{ order.charges_may | currency }}</span>
        </div>
        <div :class="{'charge':true, 'not-zero bold': order.charges_jun !== '0.00'}">
          Jun: <span v-if="order.charges_jun !== '0.00'">{{ order.charges_jun | currency }}</span>
        </div>
        <div :class="{'charge':true, 'not-zero bold': order.charges_jul !== '0.00'}">
          Jul: <span v-if="order.charges_jul !== '0.00'">{{ order.charges_jul | currency }}</span>
        </div>
        <div :class="{'charge':true, 'not-zero bold': order.charges_aug !== '0.00'}">
          Aug: <span v-if="order.charges_aug !== '0.00'">{{ order.charges_aug | currency }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex"

export default {
  name: 'order',
  props: ['orderId'],
  mounted() {
    if (this.order === false) {
      this.$store.dispatch('LOAD_ORDER', this.$route.params.id).then(data => {
        this.$forceUpdate();
        this.account_number = this.order.account_number
      });
    }
    else {
      this.account_number = this.order.account_number
    }
    this.dateInterval = setInterval(function () {
      this.currentDate = new Date();
    }.bind(this), 1000)
  },
  beforeDestroy() {
    clearInterval(this.dateInterval)
  },
  data() {
    return {
      account_number: null,
      updating: false,
      dateInterval: 0,
      currentDate: new Date()
    }
  },
  methods: {
    highlightAddressNumber(address) {
      let match = address.match(/^\d+|\d+\b|\d+(?=\w)/);
      return match !== null
        ? address.replace(/^\d+|\d+\b|\d+(?=\w)/, "<strong>" + match[0] + "</strong>")
        : address;
    },
    updateAccountNumber() {
      this.updating = true;
      this.$store.dispatch('UPDATE_ACCOUNT_NUMBER', {
        order_id: this.order.id,
        account_number: this.account_number
      })
          .then(data => {
            this.updating       = false;
            this.account_number = data.order.account_number
          })
          .catch(error => {
            this.updating = false;
          })
    },
    getItemsForRecipient(recipient) {
      return this.order.items.filter(item => {
        return item.order_recipient_id === recipient.id
      })
    },
    getProduct(id) {
      return this.products.find(product => {
        return product.id === id;
      })
    },
    getMonthName(month) {
      let baseMonth = this.months.find(mth => mth.month_name === month.month);
      if (baseMonth.month_parts.split(',').length < 2) {
        return month.month.substr(0, 3)
      }
      else {
        return month.week
      }
    },
    selectMonthCharge(month) {
      return this.order['charges_' + month.toLowerCase()];
    }
  },
  computed: Object.assign({}, mapGetters(['companies', 'company', 'orders', 'products', 'productParts', 'months']), {
    order() {
      let order = (this.$store.state.orders.all.find((order) => {
        return order.id === parseInt(this.orderId)
      }) || this.$store.state.orders.past_orders.find((order) => {
        return order.id === parseInt(this.orderId)
      }));

      if (typeof order.payments === 'undefined') {
        return false;
      }

      return order;
    },
    numShip() {
      let shipMax   = 20;
      let totalShip = '';
      let numItems  = 0;
      for (let y = 0; y < this.order.items.length; y++) {
        if (this.order.items[y].product_sku === 'SHC') {
          numItems += 10 * this.order.items[y].quantity;
        }
        else if (this.order.items[y].product_sku === 'WHC') {
          numItems += 6 * this.order.items[y].quantity;
        }
        else if (this.order.items[y].product_sku === 'SPN' ||
                 this.order.items[y].product_sku === 'SPKNF' ||
                 this.order.items[y].product_sku === 'GFK') {
          numItems += 0;
        }
        else {
          numItems += this.order.items[y].quantity * this.order.items[y].months.length;
        }
      }
      let repeatShip = Math.floor(numItems / shipMax);
      let modShip    = numItems % shipMax;
      if (repeatShip > 0) {
        for (let x = 0; x < repeatShip; x++) {
          totalShip += 'SHIP' + shipMax + (x != repeatShip ? ' - ' : '');
        }
      }
      totalShip += 'SHIP' + modShip;

      return totalShip;
    }
  })
};
</script>



// WEBPACK FOOTER //
// Order.vue?2685bec8